import { pipe } from 'fp-ts/lib/pipeable';
import { chain, map } from 'fp-ts/lib/TaskEither';
import { LoggingInUser } from '@/entities/auth/loggingInUser';
import { LoggingInSsoUser } from '@/entities/auth/loggingInSsoUser';
import { RegisteringUser } from '@/entities/auth/registeringUser';
import {
  addManualAuthorizationHeaders,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createGetRequest,
  createPostRequest,
  createPutRequest,
  decode,
  fetchApi,
  jsonHeaders,
  setBody,
} from '@/services/api';
import { ForgottenPasswordUser } from '@/entities/auth/forgottenPasswordUser';
import { PasswordResettingUser } from '@/entities/auth/passwordResettingUser';
import { PasswordChangingUser } from '@/entities/auth/passwordChangingUser';
import { ProfileUpdatingUser } from '@/entities/auth/profileUpdatingUser';
import { user as userDecoder } from './user';
import { ssoRedirect as ssoRedirectDecoder } from './ssoRedirect';
import { passwordResetCheckingUser as passwordResetCheckingUserDecoder } from './passwordResetCheckingUser';
import { token as tokenDecoder, transform as transformToken } from './token';
import { createAuthorizedPutRequest } from '../request';
import { profileUpdatingUser as profileUpdatingUserDecoder } from './profileUpdatingUser';
import { ReminderConfiguration, TimeWindow } from '@/entities/auth/reminderConfiguration';
import { configuration, timeWindow } from './notificationSettings';
import { timezonesArray } from '@/services/api/auth/timezones';

const baseUrl = '/v2/auth';
const profileUrl = '/v2/profile';
const timezoneUrl = '/v2/timezone';
const timezoneStoreUrl = '/v2/user-timezone';
const decodeToken = decode(tokenDecoder);
const decodeUser = decode(userDecoder);
const decodeSsoRedirect = decode(ssoRedirectDecoder);
const decodePasswordResetCheckingUser = decode(passwordResetCheckingUserDecoder);
const decodeProfileUpdatingUser = decode(profileUpdatingUserDecoder);
const decodeNotificationSettings = decode(configuration);
const decodeTimeWindowSettings = decode(timeWindow);
const decodeTimezones = decode(timezonesArray);

const encodeProfileUpdatingUser = profileUpdatingUserDecoder.encode;
export const loginSsoUrl = (user: LoggingInSsoUser) => `/v2/sso/start?email=${encodeURIComponent(user.email)}`;

export const login = (user: LoggingInUser) => pipe(
  `${baseUrl}/login`,
  jsonHeaders(createPostRequest),
  setBody(user),
  fetchApi,
  chain(decodeToken),
  map(transformToken),
);

export const loginSso = (user: LoggingInSsoUser) => pipe(
  loginSsoUrl(user),
  jsonHeaders(createGetRequest),
  fetchApi,
  chain(decodeSsoRedirect),
);

export const verifyOtp = (token: string) => pipe(
  `/v2/sso/verify?token=${token}`,
  jsonHeaders(createGetRequest),
  fetchApi,
  chain(decodeToken),
);

export const fetchUser = () => pipe(
  profileUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeUser),
);

export const getProfile = () => pipe(
  profileUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeProfileUpdatingUser),
);

export const getTimezones = () => pipe(
  timezoneUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeTimezones),
);

export const setUserTimezone = (timezone: string) => pipe(
  timezoneStoreUrl,
  createAuthorizedPostRequest,
  map(setBody({ timezone })),
  chain(fetchApi),
);

export const getDownloadToken = () => pipe(
  '/v2/auth/download',
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeToken),
  map(({ token }) => token),
);

export const updateProfile = (user: ProfileUpdatingUser) => pipe(
  profileUrl,
  createAuthorizedPutRequest,
  map(setBody(encodeProfileUpdatingUser(user))),
  chain(fetchApi),
);

export const updateMobileAppDownloaded = () => pipe(
  `${profileUrl}/mobile`,
  createAuthorizedPutRequest,
  chain(fetchApi),
);

export const updateIntercomTourShown = (id: string) => pipe(
  `${profileUrl}/intercom/${id}`,
  createAuthorizedPutRequest,
  chain(fetchApi),
);

export const updatePassword = (user: PasswordChangingUser) => pipe(
  `${profileUrl}/password`,
  createAuthorizedPutRequest,
  map(setBody(user)),
  chain(fetchApi),
);

export const logout = () => pipe(
  `${baseUrl}/logout`,
  createAuthorizedPostRequest,
  chain(fetchApi),
);

export const register = (user: RegisteringUser, plan: Record<string, unknown>, hsCookie: string|null) => pipe(
  `${baseUrl}/register`,
  jsonHeaders(createPostRequest),
  setBody({ ...user, plan, hsCookie }),
  fetchApi,
  chain(decodeToken),
  map(transformToken),
);

export const initiatePasswordReset = (user: ForgottenPasswordUser) => pipe(
  `${baseUrl}/reset`,
  jsonHeaders(createPostRequest),
  setBody(user),
  fetchApi,
);

type ResettingUserWithToken = { user: PasswordResettingUser; token: string };

export const executePasswordReset = ({ user, token }: ResettingUserWithToken) => pipe(
  `${baseUrl}/reset`,
  jsonHeaders(createPutRequest),
  addManualAuthorizationHeaders(token),
  setBody({ password: user.password }),
  fetchApi,
  chain(decodeToken),
  map(transformToken),
);

export const checkPasswordReset = (token: string) => pipe(
  `${baseUrl}/reset`,
  jsonHeaders(createGetRequest),
  addManualAuthorizationHeaders(token),
  fetchApi,
  chain(decodePasswordResetCheckingUser),
);

export const getNotificationSettings = () => pipe(
  `${profileUrl}/notification-settings`,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeNotificationSettings),
);

export const updateNotificationSettings = (notificationSettings: ReminderConfiguration) => pipe(
  `${profileUrl}/notification-settings`,
  createAuthorizedPutRequest,
  map(setBody(notificationSettings)),
  chain(fetchApi),
);

export const getTimeSettings = () => pipe(
  `${profileUrl}/time-window-settings`,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeTimeWindowSettings),
);

export const updateTimeWindowSettings = (timeWindowSettings : TimeWindow[]) => pipe(
  `${profileUrl}/time-window-settings`,
  createAuthorizedPutRequest,
  map(setBody(timeWindowSettings)),
  chain(fetchApi),
);
